import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../api/client';

// Notification telle que renvoyée par le backend (GET /me/notifications).
export interface ApiNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  unread: boolean;
  createdAt: string | null;
}

interface NotificationsContextType {
  items: ApiNotification[];
  unread: number;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setItems([]); setError(false); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await apiFetch<{ notifications: ApiNotification[] }>('/me/notifications', { token, auth: true });
      setItems(data?.notifications ?? []);
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken]);

  useEffect(() => { void refresh(); }, [refresh]);

  const markRead = useCallback(async (id: number) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));
    const token = getAccessToken();
    if (!token) return;
    try { await apiFetch(`/me/notifications/${id}/read`, { method: 'POST', token, auth: true }); } catch (_) {}
  }, [getAccessToken]);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(n => ({ ...n, unread: false })));
    const token = getAccessToken();
    if (!token) return;
    try { await apiFetch('/me/notifications/read-all', { method: 'POST', token, auth: true }); } catch (_) {}
  }, [getAccessToken]);

  const unread = items.reduce((n, it) => n + (it.unread ? 1 : 0), 0);

  return (
    <NotificationsContext.Provider value={{ items, unread, loading, error, refresh, markRead, markAllRead }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
  return ctx;
}
